import {useMemo} from 'react';

import {RampIdType} from '../../../generated/graphql';
import type * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import {useEntityRampFlag} from '../../../util/useRampFlag';

const RUN_DISPLAY_NAME_COL = 'run:displayName';

// Run attributes (as server paths) whose grouping the backend's SQL fast path
// supports; anything else falls back to in-memory grouping server-side. Must
// stay in sync with flatRunGroupSummaryRunKeys in
// services/gorilla/mysql/flat_runs_metadata.go.
const LEAN_GROUPED_RUN_ATTRIBUTES = new Set([
  'displayName',
  'group',
  'host',
  'jobType',
  'name',
  'state',
  'sweep',
  'sweepName',
  'username',
]);

export function supportsLeanGroupedRunsQuery(grouping: RunTypes.Key[]) {
  if (grouping.length === 0) {
    return false;
  }
  // Compare server paths (not display names) since that is what the backend
  // receives as groupKeys.
  return grouping.every(
    key =>
      key.section === 'config' ||
      (key.section === 'run' &&
        LEAN_GROUPED_RUN_ATTRIBUTES.has(Run.keyToServerPath(key)))
  );
}

export function shouldUseLeanGroupedRunsQuery({
  grouping,
  hasPinnedColumns,
  isRampEnabled,
  isTableCollapsed,
}: {
  grouping: RunTypes.Key[];
  hasPinnedColumns: boolean;
  /** Statsig entity gate `run_selector_sql_grouped_runs_entity` */
  isRampEnabled: boolean;
  isTableCollapsed: boolean;
}) {
  return (
    isRampEnabled &&
    grouping.length > 0 &&
    isTableCollapsed &&
    !hasPinnedColumns &&
    supportsLeanGroupedRunsQuery(grouping)
  );
}

/**
 * True when a column other than the run name is pinned. Pinned columns need
 * full run metadata, which the lean grouped query does not fetch.
 */
export function hasPinnedColumnsForLeanQuery(
  columnPinned: RunFeed.Config['columnPinned']
): boolean {
  return Object.entries(columnPinned).some(
    ([accessor, pinned]) => pinned && accessor !== RUN_DISPLAY_NAME_COL
  );
}

/**
 * Whether the entity is ramped into the lean grouped-runs query. Same entity
 * gate the backend checks before using SQL group aggregation
 * (run.go canUseSQLGroupSummary), so one flag controls the whole lean path.
 */
export function useLeanGroupedRunsQueryRamp(entityName: string): boolean {
  return useEntityRampFlag(
    entityName,
    'run_selector_sql_grouped_runs_entity',
    RampIdType.EntityName
  );
}

/**
 * Decides whether the run selector should fetch grouped rows via the lean
 * `runSelectorGroups` query instead of the full runs query.
 */
export function useLeanGroupedRunsQuery({
  entityName,
  grouping,
  hasPinnedColumns,
  isTableCollapsed,
}: {
  entityName: string;
  grouping: RunTypes.Key[];
  hasPinnedColumns: boolean;
  isTableCollapsed: boolean;
}): boolean {
  const isRampEnabled = useLeanGroupedRunsQueryRamp(entityName);
  return useMemo(
    () =>
      shouldUseLeanGroupedRunsQuery({
        grouping,
        hasPinnedColumns,
        isRampEnabled,
        isTableCollapsed,
      }),
    [grouping, hasPinnedColumns, isRampEnabled, isTableCollapsed]
  );
}
